import { Controller } from "@hotwired/stimulus";
import * as d3 from "d3";

export default class extends Controller {
  static values = {
    startValue: Number,
    benchmark: Number,
    trajectory: Number
  };

  static targets = [
    "arrButton",
    "verticalSelect",
    "trajectorySlider",
    "trajectoryDisplay",
    "chartContainer",
    "m12Trajectory",
    "m12Benchmark",
    "m12Difference",
    "m36Trajectory",
    "m36Benchmark",
    "m36Difference"
  ];

  connect() {
    console.info("Two::connect")
    this.updateArrButtonStyles();
    this.updateTrajectoryDisplay();
    this.drawChart();
    this.updateDisplayValues();
  }

  updateArrButtonStyles() {
    this.arrButtonTargets.forEach(btn => {
      const val = parseFloat(btn.dataset.value);
      console.info(`Two::updateArrButtonStyles::${val} vs start::${this.startValueValue}`)
      if (val === this.startValueValue) {
        btn.classList.add("bg-primary", "text-primary-foreground");
        btn.classList.remove("border-border", "hover:bg-accent");
      } else {
        btn.classList.remove("bg-primary", "text-primary-foreground");
        btn.classList.add("border-border", "hover:bg-accent");
      }
    });
  }

  updateTrajectoryDisplay() {
    if (this.hasTrajectoryDisplayTarget) {
      this.trajectoryDisplayTarget.textContent = `${Math.round(this.trajectoryValue * 100)}%`;
    }
  }

  handleArrClick(e) {
    this.startValueValue = parseFloat(e.currentTarget.dataset.value);
    console.info(`Two::handleArrClick::${this.startValueValue}`)
    this.updateArrButtonStyles();
    this.drawChart();
    this.updateDisplayValues();
  }

  updateBenchmark(e) {
    this.benchmarkValue = parseFloat(e.target.value);
    this.drawChart();
    this.updateDisplayValues();
  }

  updateTrajectory(e) {
    this.trajectoryValue = parseFloat(e.target.value);
    this.updateTrajectoryDisplay();
    this.drawChart();
    this.updateDisplayValues();
  }

  updateDisplayValues() {
    const m12Traj = this.calculateValue(this.startValueValue, this.trajectoryValue, 12);
    const m12Bench = this.calculateValue(this.startValueValue, this.benchmarkValue, 12);
    const m12Diff = m12Traj - m12Bench;

    const m36Traj = this.calculateValue(this.startValueValue, this.trajectoryValue, 36);
    const m36Bench = this.calculateValue(this.startValueValue, this.benchmarkValue, 36);
    const m36Diff = m36Traj - m36Bench;

    this.m12TrajectoryTarget.textContent = this.formatMoney(m12Traj);
    this.m12BenchmarkTarget.textContent = this.formatMoney(m12Bench);
    this.m12DifferenceTarget.textContent = `${m12Diff >= 0 ? '+' : ''}${this.formatMoney(m12Diff)}`;
    this.m12DifferenceTarget.className = `font-semibold ${m12Diff >= 0 ? 'text-green-600' : 'text-red-600'}`;

    this.m36TrajectoryTarget.textContent = this.formatMoney(m36Traj);
    this.m36BenchmarkTarget.textContent = this.formatMoney(m36Bench);
    this.m36DifferenceTarget.textContent = `${m36Diff >= 0 ? '+' : ''}${this.formatMoney(m36Diff)}`;
    this.m36DifferenceTarget.className = `font-semibold ${m36Diff >= 0 ? 'text-green-600' : 'text-red-600'}`;
  }

  calculateValue(start, multiplier, month) {
    const monthlyRate = Math.pow(multiplier, 1 / 12);
    return start * Math.pow(monthlyRate, month);
  }

  formatMoney(n) {
    if (n >= 1_000_000_000) return `$${(n / 1_000_000_000).toFixed(1)}B`;
    if (n >= 1_000_000) return `$${(n / 1_000_000).toFixed(1)}M`;
    if (n >= 1_000) return `$${(n / 1_000).toFixed(1)}K`;
    if (n <= -1_000_000_000) return `$${(n / 1_000_000_000).toFixed(1)}B`;
    if (n <= -1_000_000) return `$${(n / 1_000_000).toFixed(1)}M`;
    if (n <= -1_000) return `$${(n / 1_000).toFixed(1)}K`;

    return `$${n.toFixed(0)}`;
  }

  buildSeries(start, annualMultiplier) {
    const points = [];
    const monthlyRate = Math.pow(annualMultiplier, 1 / 12);
    for (let m = 0; m <= 36; m++) {
      points.push({ month: m, value: start * Math.pow(monthlyRate, m) });
    }
    return points;
  }

  drawChart() {
    const container = this.chartContainerTarget;
    container.innerHTML = "";

    const width = container.clientWidth;
    const height = 360;
    const margin = { top: 24, right: 32, bottom: 48, left: 72 };
    const innerW = width - margin.left - margin.right;
    const innerH = height - margin.top - margin.bottom;

    const svg = d3.select(container).append("svg")
      .attr("width", "100%")
      .attr("height", height)
      .attr("viewBox", `0 0 ${width} ${height}`);

    const g = svg.append("g").attr("transform", `translate(${margin.left},${margin.top})`);

    const benchmark = this.buildSeries(this.startValueValue, this.benchmarkValue);
    const trajectory = this.buildSeries(this.startValueValue, this.trajectoryValue);

    const x = d3.scaleLinear().domain([0, 36]).range([0, innerW]);
    const maxY = d3.max([...benchmark, ...trajectory], d => d.value) ?? this.startValueValue;
    const minY = d3.min([...benchmark, ...trajectory], d => d.value) ?? 0;
    const spread = Math.max(maxY - this.startValueValue, this.startValueValue - minY);
    const pad = spread * 0.15;
    const y = d3.scaleLinear()
      .domain([this.startValueValue - spread - pad, this.startValueValue + spread + pad])
      .range([innerH, 0]);

    const styles = getComputedStyle(document.documentElement);
    const hsl = v => `hsl(${styles.getPropertyValue(v).trim()})`;
    const primary = hsl("--primary");
    const secondary = hsl("--secondary");
    const muted = hsl("--muted-foreground");
    const border = hsl("--border");

    const quarters = d3.range(0, 37, 3);
    g.append("g").attr("class", "grid")
      .selectAll("line").data(quarters).enter().append("line")
      .attr("x1", d => x(d)).attr("x2", d => x(d))
      .attr("y1", 0).attr("y2", innerH)
      .attr("stroke", border)
      .attr("stroke-width", d => d % 12 === 0 ? 1.2 : 0.6)
      .attr("stroke-dasharray", d => d % 12 === 0 ? "0" : "2,3");

    g.append("g").selectAll("line").data(y.ticks(5)).enter().append("line")
      .attr("x1", 0).attr("x2", innerW)
      .attr("y1", d => y(d)).attr("y2", d => y(d))
      .attr("stroke", border).attr("stroke-opacity", 0.4);

    const xLabels = [{ m: 0, label: "Now" }, { m: 12, label: "M12" }, { m: 24, label: "M24" }, { m: 36, label: "M36" }];
    g.append("g").attr("transform", `translate(0,${innerH})`)
      .selectAll("text").data(xLabels).enter().append("text")
      .attr("x", d => x(d.m)).attr("y", 24)
      .attr("text-anchor", "middle").attr("fill", muted).attr("font-size", 12)
      .text(d => d.label);

    g.append("line").attr("x1", 0).attr("x2", innerW).attr("y1", innerH).attr("y2", innerH).attr("stroke", muted);

    g.append("g").selectAll("text").data(y.ticks(5)).enter().append("text")
      .attr("x", -12).attr("y", d => y(d)).attr("dy", "0.32em")
      .attr("text-anchor", "end").attr("fill", muted).attr("font-size", 12)
      .text(d => this.formatMoney(d));

    const line = d3.line()
      .x(d => x(d.month))
      .y(d => y(d.value))
      .curve(d3.curveMonotoneX);

    console.info(`Two::drawChart::${JSON.stringify(benchmark)}`)
    g.append("path").datum(benchmark)
      .attr("fill", "none").attr("stroke", primary).attr("stroke-width", 2.5)
      .attr("stroke-dasharray", "4,4").attr("d", line);

    g.append("path").datum(trajectory)
      .attr("fill", "none").attr("stroke", secondary).attr("stroke-width", 3)
      .attr("d", line);

    const legend = g.append("g").attr("transform", `translate(${innerW - 180}, 8)`);
    legend.append("line").attr("x1", 0).attr("x2", 24).attr("y1", 6).attr("y2", 6)
      .attr("stroke", primary).attr("stroke-width", 2.5).attr("stroke-dasharray", "4,4");
    legend.append("text").attr("x", 30).attr("y", 10).attr("fill", muted).attr("font-size", 12).text("Benchmark");
    legend.append("line").attr("x1", 0).attr("x2", 24).attr("y1", 26).attr("y2", 26)
      .attr("stroke", secondary).attr("stroke-width", 3);
    legend.append("text").attr("x", 30).attr("y", 30).attr("fill", muted).attr("font-size", 12).text("Your trajectory");
  }
}